import {
  put,
  all,
  call,
  fork,
  take,
  cancel,
  select,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import {
  getFirestore,
  collection,
  query,
  where,
  documentId,
  Timestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import moment from 'moment';
import toastr from 'toastr';
import rsf from '../../helpers/firebase';
import i18n from '../../i18n';
import { toDateFirebase, arrayChunks } from '../../helpers/sharedFunction';
import { LIVE_STATUS } from '../../config/aiConversationStatus';
import types from './actionTypes';
import * as actions from './actions';

const db = getFirestore(rsf.app);

const toDate = (snap, data, key) =>
  data[key] ? toDateFirebase(snap, data, key).toDate() : null;

// The transformer of store/aiConversations, plus windowRestartedAt: without
// it a conversation the agent just took back would be measured from its
// birth, and shown as already out of time.
const conversationTransformer = (snap) => {
  const data = snap.data();
  return {
    id: snap.id,
    ...data,
    startedAt: toDate(snap, data, 'startedAt'),
    endedAt: toDate(snap, data, 'endedAt'),
    stoppedAt: toDate(snap, data, 'stoppedAt'),
    handoffAt: toDate(snap, data, 'handoffAt'),
    lastLeadMessageAt: toDate(snap, data, 'lastLeadMessageAt'),
    windowRestartedAt: toDate(snap, data, 'windowRestartedAt'),
  };
};

const oldestFirst = (a, b) => (a.startedAt || 0) - (b.startedAt || 0);

function* syncLiveSaga() {
  const companyId = yield select((state) => state.Dashboard.companyId);

  // Equalities only, sorted here: companyId == and status in ride Firestore's
  // own indexes, while an orderBy would want a composite one to deploy.
  const liveRef = query(
    collection(db, 'aiConversations'),
    where('companyId', '==', companyId),
    where('status', 'in', LIVE_STATUS),
  );

  const task = yield fork(rsf.firestore.syncCollection, liveRef, {
    successActionCreator: actions.syncLiveAiConversationsSuccess,
    failureActionCreator: actions.syncLiveAiConversationsFailure,
    transform: (snap) =>
      snap.docs
        .map(conversationTransformer)
        // A stopped conversation is on its way out: the scheduler hands it
        // over on its next round. It is not the agent's any more.
        .filter(({ stoppedAt }) => !stoppedAt)
        .sort(oldestFirst),
  });

  yield take(types.RESET_AI_CONVERSATION_MONITOR_STATE);
  yield cancel(task);
}

function* syncTodaySaga() {
  const companyId = yield select((state) => state.Dashboard.companyId);

  // Needs the (companyId, startedAt) index of the functions repo. The day is
  // the browser's: the page asks again when it changes (Conversations.js).
  const todayRef = query(
    collection(db, 'aiConversations'),
    where('companyId', '==', companyId),
    where(
      'startedAt',
      '>=',
      Timestamp.fromDate(moment().startOf('day').toDate()),
    ),
  );

  const task = yield fork(rsf.firestore.syncCollection, todayRef, {
    successActionCreator: actions.syncTodayAiConversationsSuccess,
    failureActionCreator: actions.syncTodayAiConversationsFailure,
    transform: (snap) => snap.docs.map(conversationTransformer),
  });

  yield take(types.RESET_AI_CONVERSATION_MONITOR_STATE);
  yield cancel(task);
}

/**
 * The names of the leads on screen, read once each: every live snapshot asks
 * only for the ids not seen yet, in groups of thirty as `in` allows.
 */
function* fetchMissingLeadsSaga({ conversations }) {
  const known = yield select((state) => state.AiConversationMonitor.leads);
  const missing = [
    ...new Set(conversations.map(({ leadId }) => leadId).filter(Boolean)),
  ].filter((id) => !known[id]);

  if (!missing.length) return;

  yield put(actions.fetchMonitorLeads(missing));
  try {
    let leads = [];
    for (const chunk of arrayChunks(missing, 30)) {
      const leadsSnap = yield call(
        rsf.firestore.getCollection,
        query(collection(db, 'leads'), where(documentId(), 'in', chunk)),
      );
      leads = leads.concat(
        leadsSnap.docs.map((lead) => ({
          id: lead.id,
          name: lead.get('name') || '',
          phone: lead.get('phone') || '',
        })),
      );
    }
    // A lead that no longer exists is remembered empty, or every snapshot
    // would ask for it again.
    const found = new Set(leads.map(({ id }) => id));
    missing
      .filter((id) => !found.has(id))
      .forEach((id) => leads.push({ id, name: '', phone: '' }));
    yield put(actions.fetchMonitorLeadsSuccess(leads));
  } catch (error) {
    yield put(actions.fetchMonitorLeadsFailure(error));
  }
}

export const callStop = (payload) =>
  httpsCallable(
    getFunctions(),
    'stopAiConversations-stopAiConversations',
  )(payload);

export function* takeBackSaga({ conversationIds }) {
  // Never an empty list: it is what the callable reads as the emergency brake
  // on the whole company, and for an administrator it would be obeyed. The
  // live list can empty a selection while its confirmation is still open.
  if (!Array.isArray(conversationIds) || !conversationIds.length) return;

  try {
    const companyId = yield select((state) => state.Dashboard.companyId);
    const { data } = yield call(callStop, { companyId, conversationIds });
    if (data.error) throw new Error(data.error.message);

    const { stopped, notified, failed, truncated } = data.data;
    yield put(actions.takeBackAiConversationsSuccess(data.data));

    toastr.success(
      `${i18n.t('Taken back to the call center')}: ${stopped}. ${i18n.t(
        'Told on WhatsApp',
      )}: ${notified}.`,
      '',
    );
    if (failed)
      toastr.warning(
        i18n.t('Some conversations could not be taken back: try again'),
        '',
      );
    if (truncated)
      toastr.warning(i18n.t('Not all of them fit in one go: try again'), '');
  } catch (error) {
    yield put(actions.takeBackAiConversationsFailure(error));
    toastr.error(error.message, 'Error');
  }
}

export function* stopAllSaga({ agentId }) {
  try {
    const companyId = yield select((state) => state.Dashboard.companyId);
    // No conversationIds: that is what makes it the emergency brake, and
    // what the callable keeps to administrators.
    const { data } = yield call(callStop, {
      companyId,
      ...(agentId ? { agentId } : {}),
    });
    if (data.error) throw new Error(data.error.message);

    const { stopped, truncated } = data.data;
    yield put(actions.stopAllAiConversationsSuccess(data.data));

    toastr.success(`${i18n.t('Conversations stopped')}: ${stopped}.`, '');
    if (truncated)
      toastr.warning(i18n.t('Not all of them fit in one go: try again'), '');
  } catch (error) {
    yield put(actions.stopAllAiConversationsFailure(error));
    toastr.error(error.message, 'Error');
  }
}

function* aiConversationMonitorSaga() {
  yield all([
    // takeLatest on both listeners: asking again cancels the previous one,
    // which is how the today listener moves on at midnight.
    takeLatest(types.SYNC_LIVE_AI_CONVERSATIONS.REQUEST, syncLiveSaga),
    takeLatest(types.SYNC_TODAY_AI_CONVERSATIONS.REQUEST, syncTodaySaga),
    takeLatest(types.SYNC_LIVE_AI_CONVERSATIONS.SUCCESS, fetchMissingLeadsSaga),
    // takeLeading on the two writes: a second press while the callable runs
    // is a double click, not a change of mind.
    takeLeading(types.TAKE_BACK_AI_CONVERSATIONS.REQUEST, takeBackSaga),
    takeLeading(types.STOP_ALL_AI_CONVERSATIONS.REQUEST, stopAllSaga),
  ]);
}

export default aiConversationMonitorSaga;
